import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps } from '@mantine/core'

import useStyles from './index.styles'
import { MainValue } from './MainValue'
import { AirPopulationContext } from '../context'

export const Chart = memo(({ styles, classNames }: BoxProps) => {
  const {
    data,
    firstLoading,
    size,
    borderOutsideColor,
    backgroundChartColor,
  } = useContext(AirPopulationContext)
  const {
    color = 'gray',
    valueColorIndex = 7,
    statusColorIndex = 6,
    borderEndColorIndex = 7,
    borderStartColorIndex = 3,
    category,
    currentAqiusValue = 0,
  } = data || {}

  const angleObj = useMemo(() => {
    const maxValue = 500
    const circleAngle = 360
    const gapAngleLeft = 45
    const gapAngle = gapAngleLeft * 2
    const actualCircleAngle = circleAngle - gapAngle
    const endCircleAngle = gapAngleLeft + actualCircleAngle
    const selectedDegByActualDeg = Math.min(
      ((currentAqiusValue || 0) / maxValue) * actualCircleAngle,
      actualCircleAngle,
    )
    const selectedDeg = firstLoading
      ? 120
      : selectedDegByActualDeg + gapAngleLeft
    return {
      gapAngleLeft,
      selectedDeg,
      endCircleAngle,
      circleAngle,
    }
  }, [currentAqiusValue, firstLoading])

  const { classes, cx } = useStyles(
    {
      color: firstLoading ? 'neutral' : color,
      borderEndColorIndex,
      borderStartColorIndex,
      statusColorIndex,
      valueColorIndex,
      firstLoading,
      size,
      borderOutsideColor,
      backgroundChartColor,
      ...angleObj,
    },
    { name: 'AirPopulation__Chart', styles, classNames },
  )

  const [displayDeg, setDisplayDeg] = useState(angleObj.selectedDeg)
  const animationRef = useRef<number | null>(null)

  useEffect(() => {
    const duration = 200 // ms
    const startDeg = displayDeg
    const endDeg = angleObj.selectedDeg
    const diff = endDeg - startDeg
    const startTime = performance.now()

    const animate = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      const newDeg = startDeg + diff * easeOut
      setDisplayDeg(newDeg)
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      }
    }

    cancelAnimationFrame(animationRef.current!)
    animationRef.current = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationRef.current!)
  }, [angleObj.selectedDeg])

  return (
    <Box
      className={cx(classes.root, 'chart-root')}
      style={{ '--deg': `${displayDeg}deg` } as React.CSSProperties}
    >
      <Box className={cx(classes.border, 'chart-border')}>
        <Box className={cx(classes.borderWhite, 'chart-border-white')}>
          <Box className={cx(classes.background, 'chart-background')}>
            <Box className={classes.mainCircle}>
              {!firstLoading && (
                <>
                  <Text
                    size="title2"
                    variant="tertiary"
                    className={cx(classes.label, 'chart-label')}
                  >
                    AQI
                  </Text>
                  <MainValue key="main-value" className={classes.value} />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <div className={classes.tagArea}>
        <div className={classes.tag}>
          <Text
            size={size === 'sm' ? 'p3' : 'title2'}
            className={classes.status}
            weight="semiBold"
            color={'#fff'}
          >
            {category}
          </Text>
        </div>
      </div>
    </Box>
  )
})
